import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import config from '../config';

const API_URL = config.apiUrl;

// Renders the per-sheet append result: { added, skippedDuplicates, totalRows }
function SheetResult({ label, result }) {
  if (!result) return null;
  return (
    <div style={{ marginTop: '0.5rem' }}>
      <strong>{label}:</strong>{' '}
      <span className="badge badge-success">{result.added ?? 0} added</span>{' '}
      <span className="badge badge-neutral">{result.skippedDuplicates ?? 0} duplicates skipped</span>{' '}
      {result.totalRows != null && (
        <span className="badge badge-info">{result.totalRows} total rows</span>
      )}
    </div>
  );
}

export default function Upload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setResult(null);
    setError('');
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file first.');
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    setResult(null);
    setError('');
    try {
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.success) {
        setResult({ survey: response.data.survey, members: response.data.members });
        setFile(null);
        const input = document.getElementById('file-input');
        if (input) input.value = '';
      } else {
        setError('Upload failed.');
      }
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Another upload is already in progress. Please wait and try again.');
      } else {
        setError(err.response?.data?.error || 'Error uploading file. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">
          <CloudUploadIcon /> Upload Survey Data
        </h1>
        <p className="page-subtitle">
          Upload the KoBoToolbox 2-sheet Excel export. New rows are appended to the parquet store and
          the cache refreshes automatically.
        </p>
      </div>

      <div className="card">
        <div className="upload-area">
          <input
            id="file-input"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            disabled={uploading}
          />
          {file && (
            <div style={{ marginTop: '1rem' }}>
              <strong>Selected:</strong> {file.name}
            </div>
          )}
        </div>

        <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || !file}>
          <CloudUploadIcon fontSize="small" /> {uploading ? 'Uploading…' : 'Upload File'}
        </button>

        {result && (
          <div className="success-message">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 600 }}>
              <CheckCircleIcon fontSize="small" /> Upload complete
            </div>
            <SheetResult label="Household survey" result={result.survey} />
            <SheetResult label="Member details" result={result.members} />
            <button
              className="btn btn-ghost"
              style={{ marginTop: '0.75rem' }}
              onClick={() => navigate('/dashboard')}
            >
              Go to Dashboard
            </button>
          </div>
        )}
        {error && <div className="error-message">{error}</div>}

        <div className="info-box">
          <h3>Expected file format (2 sheets)</h3>
          <ul>
            <li>
              <strong>Sheet 1 — household survey</strong>: one row per household (KoBo export columns,
              including <code>_uuid</code>).
            </li>
            <li>
              <strong>Sheet 2 — member_details</strong>: one row per household member (including{' '}
              <code>member_unique_id</code> and <code>_submission__uuid</code>).
            </li>
          </ul>
          <h3>How appending works</h3>
          <ul>
            <li>Rows are matched on their unique id — existing rows are skipped as duplicates.</li>
            <li>Only genuinely new rows are added; you can safely re-upload the same file.</li>
            <li>Dates in Excel serial format are converted automatically on ingest.</li>
            <li>Uploads are processed one at a time; a concurrent upload is rejected.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
